import { ChatWidget } from '@ameripharma/chat-ui-web';
import type { ChatWidgetHandle } from '@ameripharma/chat-ui-web';
import { createContext, useCallback, useEffect, useRef } from 'react';
import type { ReactNode } from 'react';
import { useAppDispatch, useAppSelector } from '@/shared/hooks/store';
import {
    selectIsApChatEnabled,
    selectAIAssociateId,
    selectAuthState,
    selectIntercomAuthenticatedSession,
    selectIsCustomerAIChatEnabled,
    selectIsCodeDataLoaded,
    selectIsCodeDataSettled,
    selectOperatorIdleWarningSecs,
} from '@/store/slices/auth/selectors';
import { setIntercomAuthenticatedSession } from '@/store/slices/auth';
import { selectAccessibilityTop } from '@/store/slices/floating-icons-state/selectors';
import { setChatOpen } from '@/store/slices/chat-state';
import intercomAuthInstance from '@/services/intercom-auth';
import { store } from '@/store';

const CHAT_DOMAIN = import.meta.env.VITE_APP_PURE_DOMAIN as string;
const intercomEnabled = !!import.meta.env.VITE_INTERCOM_APP_ID;

interface IntercomContextInterface {
    shutDownIntercomContext: () => void;
    bootIntercomContext: () => void;
}

export const IntercomContext = createContext<IntercomContextInterface>({
    shutDownIntercomContext: () => { },
    bootIntercomContext: () => { },
});

export const ChatWidgetWrapper = ({ children }: { children?: ReactNode }) => {
    const dispatch = useAppDispatch();
    const isChatEnabled = useAppSelector(selectIsApChatEnabled);
    const aiAssociateId = useAppSelector(selectAIAssociateId);
    const { token, id: accountId } = useAppSelector(selectAuthState);
    const isAIChatEnabled = useAppSelector(selectIsCustomerAIChatEnabled);
    const isCodeDataLoaded = useAppSelector(selectIsCodeDataLoaded);
    const isCodeDataSettled = useAppSelector(selectIsCodeDataSettled);
    const operatorIdleWarningSecs = useAppSelector(selectOperatorIdleWarningSecs);

    const intercomAuthenticatedSession = useAppSelector(selectIntercomAuthenticatedSession);
    const top = useAppSelector(selectAccessibilityTop);

    const intercomOpen = useRef(false);
    const intercomAutoOpenRetryCount = useRef(0);
    const chatWidgetRef = useRef<ChatWidgetHandle>(null);

    // Intercom is used once code-data has settled (loaded or errored) and AI chat isn't enabled; a code-data error falls back to Intercom.
    const useIntercom = isCodeDataSettled && !isAIChatEnabled;

    const shutDownIntercom = useCallback(() => {
        try { console.log('shuttingh down intercom'); window.Intercom?.('shutdown'); } catch (e) { console.error('Error shutting down intercom', e); }
    }, []);

    const updateIntercomIFrames = useCallback(() => {
        if (!top) return;
        const bottom = `${top + 15}px`;
        ['intercom-notifications-frame', 'intercom-chat-frame', 'intercom-borderless-frame'].forEach(name => {
            const iframe = document.querySelector(`iframe[name="${name}"]`) as HTMLIFrameElement;
            if (iframe) iframe.style.setProperty('bottom', bottom, 'important');
        });
    }, [top]);

    const addPhNoCaptureToMessengerFrame = useCallback(() => {
        const iframe = document.querySelector('iframe[name="intercom-messenger-frame"]') as HTMLIFrameElement;
        if (iframe) {
            console.log("adding ph-no-capture to intercom-messenger-frame");
            iframe.classList.add('ph-no-capture');
        }
    }, []);

    const openIfSessionStorageFlagTrue = useCallback(() => {
        if (sessionStorage.getItem('chatAutoOpen') !== 'true') return;
        setTimeout(() => {
            window.Intercom?.('show');
            intercomAutoOpenRetryCount.current < 30 && setTimeout(() => {
                if (!intercomOpen.current) {
                    openIfSessionStorageFlagTrue();
                    intercomAutoOpenRetryCount.current += 1;
                } else {
                    sessionStorage.removeItem('chatAutoOpen');
                }
            }, 200);
        }, 1000);
    }, []);

    // Reposition Intercom iframes when accessibility offset changes
    useEffect(() => {
        if (!useIntercom) return;
        updateIntercomIFrames();
        addPhNoCaptureToMessengerFrame();
    }, [updateIntercomIFrames, addPhNoCaptureToMessengerFrame, useIntercom]);

    useEffect(() => {
        if (!useIntercom) return;
        const observer = new MutationObserver(mutationsList => {
            mutationsList.forEach(mutation => {
                if (mutation.type === 'childList') {
                    updateIntercomIFrames();
                    addPhNoCaptureToMessengerFrame();
                }
            });
        });
        observer.observe(document.body, { childList: true, subtree: true });
        return () => observer.disconnect();
    }, [updateIntercomIFrames, addPhNoCaptureToMessengerFrame, useIntercom]);

    // Watch for Intercom widget mount to trigger auto-open
    useEffect(() => {
        if (!useIntercom) return;
        const observer = new MutationObserver((changes, self) => {
            const addedBlocks = changes.flatMap(item =>
                [...(item.addedNodes || [])].filter(el => el.nodeName.toUpperCase() === 'DIV') as HTMLDivElement[]
            );
            if (addedBlocks.some(el => el.classList.contains('intercom-lightweight-app'))) {
                openIfSessionStorageFlagTrue();
                self.disconnect();
            }
        });
        observer.observe(document.body, { childList: true });
    }, [openIfSessionStorageFlagTrue, useIntercom]);

    // Authenticate with Intercom
    useEffect(() => {
        if (token && intercomEnabled && !intercomAuthenticatedSession && useIntercom) {
            intercomAuthInstance.post(token)
                .then(data => { if (data.web) dispatch(setIntercomAuthenticatedSession(data.web)); })
                .catch(err => console.error('Error authenticating with Intercom', err));
        }
    }, [token, intercomAuthenticatedSession, dispatch, useIntercom]);

    // Boot / shut down Intercom
    useEffect(() => {
        if (!intercomEnabled || !useIntercom) {
            shutDownIntercom();
            return;
        }
        if (intercomAuthenticatedSession) {
            shutDownIntercom();
            window.Intercom?.('boot', { app_id: import.meta.env.VITE_INTERCOM_APP_ID, intercom_user_jwt: intercomAuthenticatedSession });
            window.Intercom?.('onShow', () => { intercomOpen.current = true; });
            window.Intercom?.('onHide', () => { intercomOpen.current = false; });
        } else {
            shutDownIntercom();
        }
    }, [intercomAuthenticatedSession, useIntercom, shutDownIntercom]);

    // Cleanup on unmount
    useEffect(() => () => { shutDownIntercom(); }, [shutDownIntercom]);

    // Open AI chat widget once code data is loaded and widget is ready
    useEffect(() => {
        const configRaw = sessionStorage.getItem('chatAutoOpenConfig');
        const config = configRaw ? JSON.parse(configRaw) : null;
        const shouldOpen = sessionStorage.getItem('chatAutoOpen') === 'true' || config?.open === true;

        if (isCodeDataLoaded && isChatEnabled && aiAssociateId !== null && shouldOpen) {
            if (config?.conversationId) {
                setTimeout(() => {
                    chatWidgetRef.current?.selectConversation(config.conversationId);
                }, 1000);
            } else {
                chatWidgetRef.current?.open();
            }
            sessionStorage.removeItem('chatAutoOpen');
            sessionStorage.removeItem('chatAutoOpenConfig');
        }
    }, [isCodeDataLoaded, isChatEnabled, aiAssociateId]);

    const shutDownIntercomContext = () => { window.Intercom?.('shutdown'); };
    const bootIntercomContext = () => {
        if (!intercomEnabled) return;
        const session = store.getState().auth.intercomAuthenticatedSession;
        if (!session) return;
        window.Intercom?.('shutdown');
        window.Intercom?.('boot', { app_id: import.meta.env.VITE_INTERCOM_APP_ID, intercom_user_jwt: session });
    };

    return (
        <IntercomContext.Provider value={{ bootIntercomContext, shutDownIntercomContext }}>
            {children}
            {isChatEnabled && aiAssociateId !== null && (
                <ChatWidget
                    ref={chatWidgetRef}
                    domain={CHAT_DOMAIN}
                    tokenProvider={() => token}
                    chatConfig={
                        {
                            aiAssociateId: aiAssociateId.toString(),
                            ...(operatorIdleWarningSecs != null && { operatorIdleWarningSecs })
                        }
                    }
                    onAuthExpired={() => () => { }}
                    onStateChange={s => console.log('chat state change', s)}
                    accountId={accountId ?? 0}
                    title="Max"
                    hideTrigger
                    popupBottom={95}
                    popupRight={50}
                    className="ph-no-capture"
                    onOpenChange={(open: boolean) => {
                        dispatch(setChatOpen(open));
                    }}
                />
            )}
        </IntercomContext.Provider>
    );
};
